"use client"

import { useState } from "react"
import { X, Pencil, Trash2, Archive, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { SemanticMemory } from "@/lib/memory/semantic/types"

interface MemoryDetailPanelProps {
  memory: SemanticMemory
  onEdit: (updates: Partial<SemanticMemory>) => void
  onDelete: () => void
  onArchive: () => void
  onClose: () => void
}

export function MemoryDetailPanel({ memory, onEdit, onDelete, onArchive, onClose }: MemoryDetailPanelProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(memory.label || memory.title || "")
  const [editContent, setEditContent] = useState(memory.content || "")

  const handleSave = () => {
    onEdit({
      label: editTitle,
      content: editContent,
    } as any)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditTitle(memory.label || memory.title || "")
    setEditContent(memory.content || "")
    setIsEditing(false)
  }

  return (
    <div className="w-[400px] border-l border-[var(--border-subtle)] bg-[var(--surface)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
        <h2 className="font-semibold">Memory Details</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isEditing ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <textarea
                id="content"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full min-h-[200px] p-3 text-sm bg-background border border-[var(--border-subtle)] rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-[var(--brain)]"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-xs text-muted-foreground">Title</Label>
              <p className="text-sm font-medium mt-1">{memory.label || memory.title}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Content</Label>
              <p className="text-sm mt-1 leading-relaxed">{memory.content}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Section</Label>
              <p className="text-sm mt-1 capitalize">{memory.storage_section || memory.memoryType}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Created</Label>
              <p className="text-sm mt-1">{new Date(memory.created_at || memory.createdAt).toLocaleDateString()}</p>
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-[var(--border-subtle)] space-y-2">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel} className="flex-1 bg-transparent">
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              className="flex-1 bg-[var(--brain)] hover:bg-[var(--brain)]/90"
            >
              <Check className="h-4 w-4 mr-1" />
              Save
            </Button>
          </div>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="w-full justify-start">
              <Pencil className="h-4 w-4 mr-2" />
              Edit Memory
            </Button>
            <Button variant="outline" size="sm" onClick={onArchive} className="w-full justify-start bg-transparent">
              <Archive className="h-4 w-4 mr-2" />
              Archive Memory
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="w-full justify-start text-destructive hover:text-destructive bg-transparent"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Memory
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
